import React, {
	useMemo,
	useRef,
} from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { track } from '@amplitude/analytics-browser';
import type { Shot } from '../../../stores/types/shots';
import { GreenroomState as GS } from '../../../stores/types/types';
import Socket from '../../../connection/socket';
import useShots from '../../../contexts/shots/provider';
import { useOpenDeleteModal } from '../../../hooks/use-shot-utilities';
import { AmpEvent } from '../../../types/amplitude';
import { ROOM_STATES } from '../../../stores/reducers/settings';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import ButtonComms from '../button-comms';
import TooltipWrapper from '../../general-components/tooltips/tooltip-wrapper';

interface ShotDrawerProps {
	shotTabShot: Shot;
	socket: Socket | null;
	commsActive: boolean;
	commsDisabled: boolean;
	backstageCommsToggle: (muted: boolean) => void;
	isCurrentActiveShot: boolean;
	openEditShotModal: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export default function BottomButtons(props: ShotDrawerProps) {
	const {
		shotTabShot,
		socket,
		commsActive,
		commsDisabled,
		backstageCommsToggle,
		isCurrentActiveShot,
		openEditShotModal,
	} = props;

	const shotStartTimestamp = useSelector((s: GS) => s.ShotsReducer.shotStartTimestampEntity);
	const shots = useSelector((s: GS) => s.ShotsReducer.shots);
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const backstageCommsActive = useSelector((s: GS) => s.SettingsReducer.backstageCommsActive);

	const {
		playShot,
		getMissingFeeds,
	} = useShots();

	const handleOpenDeleteModal = useOpenDeleteModal();

	const {
		shot_id: shotId,
		shot_data: shotData,
	} = shotTabShot;

	const {
		shotFeeds,
	} = shotData;

	const playShotRef = useRef<HTMLButtonElement | null>(null);

	const clickPlayShot = () => {
		track(AmpEvent.shotChangeTab);
		if (socket) {
			playShot(shotId, socket);
		}
	};

	const missingFeedsInShot = useMemo(
		() => (
			roomState === ROOM_STATES.SHOWTIME && shotId !== 0
				? getMissingFeeds(shotFeeds)
				: ''),
		[shotFeeds, connectedUsers, shots, roomState, shotStartTimestamp],
	);

	return (<>
		<div className='position-fixed position-bottom controls-gradient shot-drawer'></div>

		<div className="position-fixed padding-left-large bottom-action-buttons shot-drawer-improvements">
			{roomState === ROOM_STATES.SHOWTIME && (
				<button
					onClick={openEditShotModal}
					className="edit-button corner-round-massive text-white margin-right-small"
				>
					<Icon name={ICONS.EDIT} size={13} color={COLORS.DEFAULT_GRAY} />
				</button>
			)}

			<button
				onClick={() => handleOpenDeleteModal(shotTabShot, socket)}
				className={classNames(
					'edit-button corner-round-massive margin-right-small',
					shots.length === 1 ? 'disabled' : '',
				)}
			>
				<Icon
					name={ICONS.TRASH}
					size={13}
					color={shots.length === 1 ? COLORS.DARK_GRAY : COLORS.DEFAULT_GRAY}
				/>
			</button>

			{roomState === ROOM_STATES.SHOWTIME && isCurrentActiveShot && (
				<ButtonComms
					active={commsActive}
					disabled={commsDisabled}
					featureToggleOn={backstageCommsActive}
					onMouseDown={() => backstageCommsToggle(false)}
					onMouseLeave={() => backstageCommsToggle(true)}
					onMouseUp={() => backstageCommsToggle(true)}
					customOnMessage="LIVE presenters"
				/>
			)}
			{roomState === ROOM_STATES.SHOWTIME && !isCurrentActiveShot && (
				<TooltipWrapper message={missingFeedsInShot}>
					<button
						onClick={clickPlayShot}
						className={classNames(
							'shot-drawer-improvements corner-round-massive play-shot-btn',
							missingFeedsInShot
								? 'background-warning text-white shot-tooltip'
								: 'background-green text-dark',
						)}
						ref={playShotRef}
					>
						<span className='margin-right-small'>Play Shot</span>
						<Icon
							name={ICONS.ARROW_RIGHT}
							size={20}
							color={COLORS.DEFAULT_GRAY}
						/>
					</button>
				</TooltipWrapper>
			)}

		</div>
	</>);
}
